/**
 * PackagesRedemptionsLog — Phase C1 (closes A4b cockpit gap).
 *
 * Daily audit log of every package session used. Per-row Undo button
 * appears only within the 24h grace window. "Restore credit" handles
 * the early-checkout boarding case (axiom-1 ramification) where the
 * original 5-night redemption stays but unconsumed nights are credited
 * back via a compensating row.
 *
 * Banner per row reminds staff that Camus (as platform seller) has
 * already issued the e-invoice — must NOT issue a second 發票
 * (REGULATORY-3: TW MOF double-issuance ban).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info, Loader2, RotateCcw, Undo2 } from 'lucide-react'

import { billing, type PackageRedemption } from '../api/client'
import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import { RestoreCreditDialog } from './RestoreCreditDialog'

const UNDO_WINDOW_HOURS = 24

function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3600_000
}

function fmtRelative(iso: string, locale: string): string {
  const h = hoursSince(iso)
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    if (h < 1) return rtf.format(-Math.round(h * 60), 'minute')
    if (h < 24) return rtf.format(-Math.round(h), 'hour')
    return rtf.format(-Math.round(h / 24), 'day')
  } catch {
    return new Date(iso).toLocaleString()
  }
}

export function PackagesRedemptionsLog() {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<PackageRedemption[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [includeVoided, setIncludeVoided] = useState(false)
  const [reload, setReload] = useState(0)
  const [voidingId, setVoidingId] = useState<string | null>(null)
  const [restoreTarget, setRestoreTarget] = useState<PackageRedemption | null>(
    null,
  )

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    billing
      .listRedemptions({ include_voided: includeVoided, limit: 100 })
      .then((r) => alive && setItems(r.items))
      .catch(() => alive && setError('load_failed'))
    return () => {
      alive = false
    }
  }, [includeVoided, reload])

  async function doVoid(r: PackageRedemption) {
    const reason = window.prompt(t('packages.redemptions.undoReasonPrompt') as string)
    if (!reason || !reason.trim()) return
    setVoidingId(r.id)
    try {
      await billing.voidRedemption(r.id, { reason: reason.trim() })
      setReload((n) => n + 1)
    } catch (e) {
      const detail = (e as { detail?: string })?.detail
      setError(detail ?? 'void_failed')
    } finally {
      setVoidingId(null)
    }
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{t('packages.redemptions.title')}</CardTitle>
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={includeVoided}
                onChange={(e) => setIncludeVoided(e.target.checked)}
              />
              {t('packages.redemptions.includeVoided')}
            </label>
          </div>
          <CardDescription className="flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            {t('packages.redemptions.einvoiceWarning')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items === null && !error && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('packages.loading')}
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive">
              {t(`packages.redemptions.errors.${error}`, error)}
            </p>
          )}
          {items && items.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('packages.redemptions.empty')}
            </p>
          )}
          {items && items.length > 0 && (
            <div className="-mx-4 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">
                      {t('packages.redemptions.colWhen')}
                    </th>
                    <th className="px-4 py-2">
                      {t('packages.redemptions.colService')}
                    </th>
                    <th className="px-4 py-2">
                      {t('packages.redemptions.colKind')}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('packages.redemptions.colSessions')}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('packages.redemptions.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const within24h = hoursSince(r.redeemed_at) < UNDO_WINDOW_HOURS
                    const isRestore = r.redemption_kind === 'restore'
                    return (
                      <tr
                        key={r.id}
                        className={`border-b last:border-0 ${r.is_voided ? 'opacity-50' : ''}`}
                        data-testid={`redemption-row-${r.id}`}
                      >
                        <td className="px-4 py-2">
                          <div>{fmtRelative(r.redeemed_at, i18n.language)}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(r.redeemed_at).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          {t(
                            `packages.serviceType.${r.snapshot_service_type}`,
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {isRestore ? (
                            <span className="inline-block rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                              {t('packages.redemptions.kindRestore')}
                            </span>
                          ) : r.is_voided ? (
                            <span className="inline-block rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                              {t('packages.redemptions.kindVoided')}
                            </span>
                          ) : (
                            <span className="inline-block rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900">
                              {t('packages.redemptions.kindRedeem')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {isRestore ? '+' : '−'}
                          {r.sessions_used}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {!r.is_voided && !isRestore && within24h && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => doVoid(r)}
                              disabled={voidingId === r.id}
                              data-testid={`undo-${r.id}`}
                            >
                              {voidingId === r.id ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <Undo2 className="mr-1 h-3 w-3" />
                              )}
                              {t('packages.redemptions.undoBtn')}
                            </Button>
                          )}
                          {!r.is_voided && !isRestore && !within24h && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRestoreTarget(r)}
                              data-testid={`restore-${r.id}`}
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />
                              {t('packages.redemptions.restoreBtn')}
                            </Button>
                          )}
                          {r.is_voided && r.void_reason && (
                            <span className="text-xs text-muted-foreground">
                              {r.void_reason}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RestoreCreditDialog
        original={restoreTarget}
        open={!!restoreTarget}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null)
        }}
        onRestored={() => {
          setRestoreTarget(null)
          setReload((n) => n + 1)
        }}
      />
    </div>
  )
}
